<template>
  <CollectionCarouselHelper
    v-if="collectionOnly && collectionHandle"
    v-slot="{ products }"
    :collection-handle="collectionHandle"
  >
    <HomepageProductCarousel
      :title="title"
      :subtitle="subtitle"
      :link="link"
      :products="products"
      :slide-width="slideWidth"
      :slide-width-tablet="slideWidthTablet"
      :slide-width-desktop="slideWidthDesktop"
    />
  </CollectionCarouselHelper>
  <div v-else :id="placementId" class="nosto_element">
    <NostoCarouselHelper
      v-if="(nostoProductRecommendations || []).length > 0"
      v-slot="{ products }"
      :recommendations="nostoProductRecommendations"
      :placement-id="nostoProductRecommendationsProductId"
    >
      <HomepageProductCarousel
        :title="title"
        :subtitle="subtitle"
        :link="link"
        :products="products"
        :slide-width="slideWidth"
        :slide-width-tablet="slideWidthTablet"
        :slide-width-desktop="slideWidthDesktop"
      />
    </NostoCarouselHelper>
    <CollectionCarouselHelper
      v-else-if="collectionHandle"
      v-slot="{ products }"
      :collection-handle="collectionHandle"
    >
      <HomepageProductCarousel
        :title="title"
        :subtitle="subtitle"
        :link="link"
        :products="products"
        slide-width="310px"
        slide-width-tablet="310px"
        slide-width-desktop="300px"
      />
    </CollectionCarouselHelper>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import NostoCarouselHelper from "@/components/NostoCarouselHelper.vue";

// Carousels created in the same tick share one frontpage load, so the top and
// bottom placements are both returned instead of the later request replacing
// the earlier one.
const pendingPlacements = new Map();
let placementLoadScheduled = false;

function loadFrontpagePlacement(placementId) {
  return new Promise((resolve, reject) => {
    const waiters = pendingPlacements.get(placementId) || [];
    waiters.push({ resolve, reject });
    pendingPlacements.set(placementId, waiters);

    if (placementLoadScheduled) {
      return;
    }

    placementLoadScheduled = true;
    queueMicrotask(() => {
      const batch = new Map(pendingPlacements);
      pendingPlacements.clear();
      placementLoadScheduled = false;

      const placementIds = [...batch.keys()];

      const fail = (error) => {
        placementIds.forEach((id) => {
          batch.get(id).forEach(({ reject: rejectWaiter }) => {
            rejectWaiter(error);
          });
        });
      };

      const nostojs =
        typeof window !== "undefined" ? window.nostojs : undefined;

      if (typeof nostojs !== "function") {
        fail(new Error("Nosto is unavailable"));
        return;
      }

      nostojs((api) => {
        api
          .defaultSession()
          .viewFrontPage()
          .setPlacements(placementIds)
          .load()
          .then((data) => {
            placementIds.forEach((id) => {
              const recommendation = data.recommendations?.[id] || null;
              batch.get(id).forEach(({ resolve: resolveWaiter }) => {
                resolveWaiter(recommendation);
              });
            });
          })
          .catch(fail);
      });
    });
  });
}

export default {
  name: "HomepageRecommendationsCarousel",
  components: {
    NostoCarouselHelper,
    HomepageProductCarousel: defineAsyncComponent(() =>
      import("@/components/homepage/HomepageProductCarousel.vue")
    ),
    CollectionCarouselHelper: defineAsyncComponent(() =>
      import("@/components/CollectionCarouselHelper.vue")
    ),
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
    link: {
      type: String,
      default: null,
    },
    slideWidth: {
      type: String,
      default: "200px",
    },
    slideWidthTablet: {
      type: String,
      default: "260px",
    },
    slideWidthDesktop: {
      type: String,
      default: "300px",
    },
    collectionHandle: {
      type: String,
      default: null,
    },
    collectionOnly: {
      type: Boolean,
      default: false,
    },
    placementId: {
      type: String,
      default: "frontpage-nosto-1",
    },
  },
  data() {
    return {
      nostoProductRecommendations: [],
      nostoProductRecommendationsProductId: null,
    };
  },
  created() {
    if (this.collectionOnly || !this.placementId) {
      return;
    }

    loadFrontpagePlacement(this.placementId)
      .then((recommendation) => {
        this.nostoProductRecommendations = recommendation?.products || null;
        this.nostoProductRecommendationsProductId =
          recommendation?.result_id || null;
      })
      .catch(() => {
        this.nostoProductRecommendations = null;
      });
  },
};
</script>
